import type { AppRoute } from './route';

export interface SeoMetadata {
  title: string;
  description: string;
  robots: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogType: string;
  ogUrl: string;
}

export const SEO_CONFIGS: Record<AppRoute, SeoMetadata> = {
  LANDING: {
    title: 'OFFICE CLASH - 定時なき戦い -',
    description: '営業部 vs 開発部。深夜のオフィス頂上決戦。ブラウザで遊べる2D対戦格闘ゲーム。',
    robots: 'index,follow',
    canonical: 'https://promptlab-games.com/office-clash/',
    ogTitle: 'OFFICE CLASH - 定時なき戦い -',
    ogDescription: '営業部 vs 開発部。深夜のオフィス頂上決戦。ブラウザで遊べる2D対戦格闘ゲーム。',
    ogType: 'website',
    ogUrl: 'https://promptlab-games.com/office-clash/',
  },
  PLAY: {
    title: 'OFFICE CLASH - 定時なき戦い - プレイ',
    description: 'OFFICE CLASHのゲームプレイ画面。営業部と開発部によるオフィス対戦格闘ゲーム。',
    robots: 'noindex,follow',
    canonical: 'https://promptlab-games.com/office-clash/play',
    ogTitle: 'OFFICE CLASH - プレイ',
    ogDescription: 'OFFICE CLASHのゲームプレイ画面。',
    ogType: 'website',
    ogUrl: 'https://promptlab-games.com/office-clash/play',
  },
  DESIGN: {
    title: 'ゲームデザイン | OFFICE CLASH - 定時なき戦い -',
    description: 'OFFICE CLASHのゲームデザインとPresentation設計。営業部・開発部のバトル特性、棒人間ビジュアル、バトルシステム。',
    robots: 'index,follow',
    canonical: 'https://promptlab-games.com/office-clash/design',
    ogTitle: 'ゲームデザイン | OFFICE CLASH - 定時なき戦い -',
    ogDescription: 'OFFICE CLASHのゲームデザインとPresentation設計。',
    ogType: 'article',
    ogUrl: 'https://promptlab-games.com/office-clash/design',
  },
  DEVELOPMENT: {
    title: '開発工程 | OFFICE CLASH - 定時なき戦い -',
    description: 'OFFICE CLASHの開発工程とアーキテクチャ発展。Phase 0からPost-P7 Polish、Web配信最適化の実装履歴。',
    robots: 'index,follow',
    canonical: 'https://promptlab-games.com/office-clash/development',
    ogTitle: '開発工程 | OFFICE CLASH - 定時なき戦い -',
    ogDescription: 'OFFICE CLASHの開発工程とアーキテクチャ発展。',
    ogType: 'article',
    ogUrl: 'https://promptlab-games.com/office-clash/development',
  },
  TESTING: {
    title: 'テスト戦略 | OFFICE CLASH - 定時なき戦い -',
    description: 'OFFICE CLASHの自動テスト戦略。Vitest、Playwright E2E、Visual Regression、Cloudflare Preview Parity検証。',
    robots: 'index,follow',
    canonical: 'https://promptlab-games.com/office-clash/testing',
    ogTitle: 'テスト戦略 | OFFICE CLASH - 定時なき戦い -',
    ogDescription: 'OFFICE CLASHの自動テスト戦略と品質保証。',
    ogType: 'article',
    ogUrl: 'https://promptlab-games.com/office-clash/testing',
  },
  ARCHITECTURE: {
    title: 'アーキテクチャ | OFFICE CLASH - 定時なき戦い -',
    description: 'OFFICE CLASHのWeb・ゲームアーキテクチャ。Core/Presentation分離、SPA Fallback、Cloudflare PagesおよびReverse Proxy Worker連携。',
    robots: 'index,follow',
    canonical: 'https://promptlab-games.com/office-clash/architecture',
    ogTitle: 'アーキテクチャ | OFFICE CLASH - 定時なき戦い -',
    ogDescription: 'OFFICE CLASHのWeb・ゲームアーキテクチャ設計。',
    ogType: 'article',
    ogUrl: 'https://promptlab-games.com/office-clash/architecture',
  },
  NOT_FOUND: {
    title: '404 ページが見つかりません | OFFICE CLASH',
    description: 'お探しのページは見つかりませんでした。',
    robots: 'noindex,nofollow',
    canonical: 'https://promptlab-games.com/office-clash/',
    ogTitle: '404 ページが見つかりません | OFFICE CLASH',
    ogDescription: 'お探しのページは見つかりませんでした。',
    ogType: 'website',
    ogUrl: 'https://promptlab-games.com/office-clash/',
  },
};

const ensureMetaTag = (attrName: string, attrValue: string): HTMLMetaElement => {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attrName}="${attrValue}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attrName, attrValue);
    document.head.appendChild(element);
  }
  return element;
};

const ensureCanonicalLink = (): HTMLLinkElement => {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  return element;
};

export const applySeoMetadata = (metadata: SeoMetadata): void => {
  document.title = metadata.title;

  const desc = ensureMetaTag('name', 'description');
  desc.setAttribute('content', metadata.description);

  const robots = ensureMetaTag('name', 'robots');
  robots.setAttribute('content', metadata.robots);

  const canonical = ensureCanonicalLink();
  canonical.setAttribute('href', metadata.canonical);

  const ogTitle = ensureMetaTag('property', 'og:title');
  ogTitle.setAttribute('content', metadata.ogTitle);

  const ogDesc = ensureMetaTag('property', 'og:description');
  ogDesc.setAttribute('content', metadata.ogDescription);

  const ogType = ensureMetaTag('property', 'og:type');
  ogType.setAttribute('content', metadata.ogType);

  const ogUrl = ensureMetaTag('property', 'og:url');
  ogUrl.setAttribute('content', metadata.ogUrl);
};
